import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { contractCategoriesApi } from "../../api/contractCategoriesApi";
import type { ContractCategory } from "../../types/contractCategories.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import { getApiErrorMessage } from "../../utils/apiError";
import { reportDeleteFailure } from "../../utils/deleteFailure";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";

interface ContractCategoriesState {
  items: ContractCategory[];
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: ContractCategoriesState = {
  items: [],
  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const contractCategoriesSlice = createSlice({
  name: "contractCategories",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setContractCategories: (
      state,
      action: PayloadAction<ContractCategory[]>,
    ) => {
      state.items = action.payload;
    },
    appendContractCategories: (
      state,
      action: PayloadAction<ContractCategory[]>,
    ) => {
      state.items = [...state.items, ...action.payload];
    },
    addSingleContractCategoryLocally: (
      state,
      action: PayloadAction<ContractCategory>,
    ) => {
      state.items.unshift(action.payload);
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetContractCategoryList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },
    updateSingleContractCategory: (
      state,
      action: PayloadAction<Partial<ContractCategory> & { id: string }>,
    ) => {
      const index = state.items.findIndex((cc) => cc.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    removeSingleContractCategory: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((cc) => cc.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,
  setContractCategories,
  appendContractCategories,
  addSingleContractCategoryLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetContractCategoryList,
  updateSingleContractCategory,
  removeSingleContractCategory,
} = contractCategoriesSlice.actions;

interface FetchContractCategoriesPayload {
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  sortAscending?: boolean;
  groupIds?: string[];
  ids?: string[];
  searchText?: string;
  includeDeleted?: boolean;
}

export const fetchContractCategories = (
  payload: FetchContractCategoriesPayload = {},
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().contractCategories;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    if (state.isLoading || (!state.hasMore && incomingPageIndex !== 0)) return;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "string",
        // This endpoint reads the flag as "ascending" despite its name — a
        // quirk every dropdown in the app relies on, so it is matched here
        // rather than changed there.
        sortDecending: payload.sortAscending ?? false,
        groupIds: payload.groupIds || [userData?.companyId],
        ids: payload.ids || [],
        searchText: payload.searchText || "",
        includeDeleted: payload.includeDeleted || false,
      };

      const response =
        await contractCategoriesApi.searchContractCategories(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setContractCategories(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendContractCategories(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Contract Categories error:", error);
      toast.error(
        i18n.t("common:slices.contractCategories.fetchError", {
          defaultValue: "Failed to fetch contract categories",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const addContractCategory = (data: { contractCategory: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));

      const payload = {
        contractCategory: data.contractCategory,
      };

      const res = await contractCategoriesApi.createContractCategory(payload);

      const createdId = res?.data?.id || res?.data || res?.id || res;

      const newContractCategory: ContractCategory = {
        contractCategory: data.contractCategory,
        id: typeof createdId === "string" ? createdId : createdId?.id,
        groupId: res?.data?.groupId || res?.groupId || "",
      };

      dispatch(addSingleContractCategoryLocally(newContractCategory));

      toast.success(
        i18n.t("common:slices.contractCategories.addSuccess", {
          defaultValue: "Contract Category created successfully",
        }),
      );
      return newContractCategory;
    } catch (error) {
      console.error("Create Contract Category error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("common:slices.contractCategories.addError", {
          defaultValue: "Failed to create contract category",
        })));
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editContractCategory = (data: {
  id: string;
  contractCategory: string;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEditing(true));

      const payload = {
        id: data.id,
        contractCategory: data.contractCategory,
      };

      const res = await contractCategoriesApi.updateContractCategory(payload);
      const updatedCategory = res?.data || payload;

      dispatch(updateSingleContractCategory(updatedCategory));

      toast.success(
        i18n.t("common:slices.contractCategories.editSuccess", {
          defaultValue: "Contract Category updated successfully",
        }),
      );
      return updatedCategory;
    } catch (error) {
      console.error("Edit Contract Category error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("common:slices.contractCategories.editError", {
          defaultValue: "Failed to update contract category",
        })));
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const removeContractCategory = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      dispatch(removeSingleContractCategory(id));

      await contractCategoriesApi.removeContractCategory(id);

      toast.success(
        i18n.t("common:slices.contractCategories.removeSuccess", {
          defaultValue: "Contract Category removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Contract Category error:", error);
      // The API refuses deletion while contracts or staff still use the
      // record and says which — show that reason, not a generic failure.
      const fallback = i18n.t("common:slices.contractCategories.removeError", {
        defaultValue: "Failed to remove contract category",
      });
      reportDeleteFailure(error, fallback);
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default contractCategoriesSlice.reducer;
